import { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { IconButton, Tooltip } from '@material-ui/core';
import { Search as PageviewIcon } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import SimpleConsultPageTemplate from '../../../../templates/simple-consult-page/SimpleConsultPageTemplate/SimpleConsultPageTemplate';
import { formatDate } from '../../../../utils/general/general-utils';
import { formatCnpj } from '../../../../utils/hooks/form/field/formatters';
import { followUpAPI } from '../../../../features/follow-up/followUpAPI';

export default function ListaProcessosContaCorrentePage({ authInfo = {} }) {
  const { t } = useTranslation();
  const history = useHistory();

  const [contaCorrenteProcessos, setContaCorrenteProcessos] = useState([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const handleConfigChange = useCallback(async ({ currentPage, pageSize, filterInputValue }) => {
    setLoading(true);

    try {
      const response = await followUpAPI.fetchContaCorrenteProcessoResumos([
        { name: 'page', value: currentPage },
        { name: 'size', value: pageSize },
        { name: 'processo', value: filterInputValue },
      ]);

      if (response.status === 200) {
        setRowsCount(Number(response.headers['x-pagination-total-count']) || 0);
        setContaCorrenteProcessos(response.data);
      }
    } catch (err) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        field: 'numero',
        headerName: t('com.muralis.qcx.processo'),
        flex: 180,
        valueGetter: ({ row }) => row?.numero ?? '-',
      },
      {
        field: 'referencia',
        headerName: t('com.muralis.qcx.referencia'),
        flex: 120,
        valueGetter: ({ row }) => row?.referenciaCliente || '-',
      },
      {
        field: 'importador',
        headerName: t('com.muralis.qcx.importador.label'),
        flex: 120,
        valueGetter: ({ row }) => row?.importadorNome?.split(' ')[0] || '-',
      },
      {
        field: 'cliente',
        headerName: t('com.muralis.qcx.cliente.label'),
        flex: 120,
        valueGetter: ({ row }) => row?.clienteFaturamentoNome?.split(' ')[0] || '-',
      },
      {
        field: 'importador.cnpj',
        headerName: t('com.muralis.qcx.empresa.CNPJ'),
        flex: 140,
        valueGetter: ({ row }) => formatCnpj(row?.importadorCnpj) || '-',
      },
      {
        field: 'servico',
        headerName: t('com.muralis.qcx.servico.labelSingular'),
        flex: 100,
        valueGetter: ({ row }) => row?.servicoDescription || '-',
      },
      {
        field: 'insertionDate',
        headerName: t('com.muralis.qcx.abertura'),
        headerAlign: 'center',
        align: 'center',
        type: 'date',
        flex: 100,
        valueGetter: ({ row }) => formatDate(row?.insertionDate, 'YYYY-MM-DD', 'DD/MM/YYYY') || '-',
      },
      {
        field: 'actions',
        headerName: t('com.muralis.qcx.acoes.label'),
        headerAlign: 'center',
        align: 'center',
        flex: 80,
        sortable: false,
        renderCell: ({ row: { id } }) => (
          <Tooltip title={t('com.muralis.qcx.acoes.consultar')}>
            <IconButton size="small" onClick={() => history.push(`/financeiro/conta-corrente-processo/${id}`)}>
              <PageviewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [history]
  );

  const templateProps = {
    pageProps: {
      title: 'Processos',
      icon: null,
      breadcrumbs: [
        { link: { to: '/', name: t('com.muralis.qcx.inicio') } },
        { text: { name: 'Processos' }, default: true },
      ],
    },
    tableProps: {
      columns,
      rowsCount,
      onConfigChangeCallback: handleConfigChange,
    },
  };

  return (
    <SimpleConsultPageTemplate
      {...templateProps}
      tableData={contaCorrenteProcessos}
      authInfo={authInfo}
      requiredRoles={['conta-corrente-processo-visualizar']}
      isLoading={loading}
    />
  );
}
